import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { DateTime } from "luxon";
import qs, { stringify } from "qs";
import * as workerTimers from "worker-timers";
import { SessionPhoto } from "../Shared/GeneratedTypes";
import { OnPremiseSettings } from "../Shared/sharedTypes";
import { clientConstraints } from "./Constraints";
import { LiveSwitchMiddlewareConnector } from "./liveSwitchMiddlewareConnector";
import { sendClientAudioMuteUpdated } from "./liveSwitchActions";

export enum Mode {
    Sfu = 1,
    Mcu = 2,
    Peer = 3,
}

// cSpell:ignore ignore nocapture
const CommandPrefix = "*!!*COMMAND*!!*";

enum ChatCommand {
    TakePhoto = "TakePhoto",
    SwitchCamera = "SwitchCamera",
    MuteCamera = "MuteCamera",
    PhotoUrl = "PhotoUrl",
    VideoFrameTagPhotoUrl = "VideoFrameTagPhotoUrl",
    MuteAudio = "MuteAudio",
    SessionEnded = "SessionEnded",
    PhotoRemoved = "PhotoRemoved",
    CanvasQuality = "CanvasQuality",
    ToggleRecording = "ToggleRecording",
    ClientConnectionQualityWarning = "ClientConnectionQualityWarning",
    ClientConnectionQualityOk = "ClientConnectionQualityOk",
    ClientMuteChanged = "ClientMuteChanged",
}

enum StreamQuality {
    a_256by144 = 1,
    b_512by288 = 2,
    c_768by432 = 3,
    d_1024by576 = 4,
    e_1280by720 = 5,
}

class LiveSwitchApp {
    private mentorVideoFrameRate = 15;
    private mentorVideoHeight = 240;
    private mentorVideoWidth = 320;

    // Track whether the user has decided to leave (unregister)
    // If they have not and the client gets into the Disconnected state then we attempt to re-register (reconnect) automatically.
    private unRegistering: boolean = false;
    private reRegisterBackoff: number = 200;
    private maxRegisterBackoff: number = 5000;
    private canvasStreamQuality: StreamQuality = StreamQuality.c_768by432;
    private readonly photoSizeMultiplierFor4x3 = 500;
    private recordingEnabled: boolean = false;

    client: fm.liveswitch.Client | null = null;
    channel!: fm.liveswitch.Channel;
    userId: string;
    deviceId: string | null = null;
    localMedia: fm.liveswitch.LocalMedia | null = null;
    canvasLocalMedia: fm.liveswitch.LocalMedia | null = null;
    layoutManager!: fm.liveswitch.DomLayoutManager;
    sfuDownstreamConnections: {
        [index: string]: {
            connection: fm.liveswitch.SfuConnection;
            info: fm.liveswitch.ConnectionInfo;
        };
    } = {};

    imageCapture!: ImageCapture;
    dataChannelsSupported: any;
    dataChannels: any;
    localVideoInputs?: fm.liveswitch.SourceInput[];
    remoteVideoInputs?: fm.liveswitch.SourceInput[];
    upStreamConnection?: fm.liveswitch.SfuUpstreamConnection;

    private remoteVideo: HTMLDivElement | null = null;
    private myVideo: HTMLDivElement | null = null;
    private channelId: string | null = null;

    userName!: string;
    roles!: string[];
    isClient: boolean = false;
    isIntObserver: boolean = false;
    isExtObserver: boolean = false;
    canvasElement: HTMLCanvasElement;
    canvas2dContext: CanvasRenderingContext2D;
    streamingCanvasElement: HTMLCanvasElement;
    streamingCanvas2dContext: CanvasRenderingContext2D;
    token: string | null = null;
    deviceType: string = "";
    connector!: LiveSwitchMiddlewareConnector;
    takingPhoto: boolean = false;
    remoteMedia: fm.liveswitch.RemoteMedia | null = null;
    videoElement: HTMLVideoElement | null = null;
    haveCapture: boolean = !!(window as any).ImageCapture;
    operatingSystem: string = "";
    timeoutId: number | null = null;
    localAudioInputs?: fm.liveswitch.SourceInput[];
    skipUDPConnections: boolean = false;
    enableAI: boolean = false;
    aiInterval: number | null = null;
    aiCanvas!: HTMLCanvasElement;
    ai2dContext!: CanvasRenderingContext2D;
    remoteVideoElement!: HTMLVideoElement;
    onPremiseSettings!: OnPremiseSettings;
    isClientConnectionQualityWarningIssued: boolean = false;

    //DO NOT REMOVE: necessary for iOS 15.1+ to work (GS-185)
    info!: HTMLDivElement;

    constructor() {
        this.userId = "CLIENT";
        this.canvasElement = document.createElement("canvas");
        // TODO Firefox has https://developer.mozilla.org/en-US/docs/Web/API/OffscreenCanvas
        this.canvas2dContext = this.canvasElement.getContext("2d")!;

        this.streamingCanvasElement = document.createElement("canvas");
        this.streamingCanvas2dContext = this.streamingCanvasElement.getContext("2d")!;

        //DO NOT REMOVE: necessary for iOS 15.1+ to work (GS-185)
        this.info = document.createElement("div");
        document.body.append(this.info);
    }

    public registerMiddlewareCallbacks(connector: LiveSwitchMiddlewareConnector) {
        this.connector = connector;
    }

    /***
     * Please note that you are not guaranteed to have fm namespace loaded until here as we load it async
     * but do not utilize until this call so it's dependency is not hit until here.
     */
    public initializeVideo(
        remoteVideo: HTMLDivElement,
        myVideo: HTMLDivElement | null,
        channelId: string,
        userName: string,
        roles: string[],
        logContainer: HTMLDivElement | null,
        liveSwitchRegistrationToken: string,
        deviceId: string,
        deviceType: string,
        operatingSystem: string,
        applicationInsights: ApplicationInsights,
        skipUDPConnections: boolean,
        enableAI: boolean,
        recordingEnabled: boolean,
        onPremiseSettings: OnPremiseSettings
    ) {
        this.remoteVideo = remoteVideo;
        this.myVideo = myVideo;
        this.deviceId = deviceId;
        this.channelId = channelId;
        this.userName = userName;
        this.roles = roles;
        this.token = liveSwitchRegistrationToken;
        this.isClient = this.roles.indexOf("Client") !== -1;
        this.isIntObserver = this.myVideo == null && this.roles.indexOf("Mentor") !== -1;
        this.isExtObserver = this.roles.indexOf("ExternalObserver") !== -1;
        this.deviceType = deviceType;
        this.operatingSystem = operatingSystem;
        this.skipUDPConnections = skipUDPConnections;
        this.enableAI = enableAI;
        this.recordingEnabled = recordingEnabled;
        this.onPremiseSettings = onPremiseSettings;

        //register log provider to make use of the LiveSwitch logging API functionality
        //this is for dev/debug only.  Remove once the Safari audio issue is resolved.
        //fm.liveswitch.Log.setDefaultLogLevel(fm.liveswitch.LogLevel.Debug);
        //fm.liveswitch.Log.registerProvider(new fm.liveswitch.ConsoleLogProvider());

        //warning-level logging already existed
        fm.liveswitch.Log.registerProvider(new fm.liveswitch.ConsoleLogProvider(fm.liveswitch.LogLevel.Warn));

        if (!this.isClient && !this.isExtObserver && logContainer) {
            fm.liveswitch.Log.registerProvider(
                new fm.liveswitch.DomLogProvider(logContainer, fm.liveswitch.LogLevel.Warn)
            );
        }

        class ApplicationInsightsLogProvider extends fm.liveswitch.LogProvider {
            constructor(private appInsights: ApplicationInsights) {
                super();
            }

            protected doLog(logEvent: fm.liveswitch.LogEvent): void {
                let severityLevel = SeverityLevel.Information;

                switch (logEvent.getLevel()) {
                    case fm.liveswitch.LogLevel.Error: {
                        severityLevel = SeverityLevel.Error;
                        break;
                    }
                    case fm.liveswitch.LogLevel.Warn: {
                        severityLevel = SeverityLevel.Warning;
                        break;
                    }
                    case fm.liveswitch.LogLevel.Fatal: {
                        severityLevel = SeverityLevel.Critical;
                        break;
                    }
                }

                this.appInsights.trackTrace({
                    message: `FM: ${logEvent.getScope() || ""} - ${logEvent.getMessage() || ""} ${logEvent.getException()?.message ?? ""}`,
                    severityLevel
                });
            }
        }

        fm.liveswitch.Log.registerProvider(new ApplicationInsightsLogProvider(applicationInsights));
    }

    public startSession() {
        this.layoutManager = new fm.liveswitch.DomLayoutManager(this!.remoteVideo!);
        if (this.myVideo !== null) {
            this.initLayoutAndLocalView(); // Skips setting up local media if my video is null, this is for the observer currently
        } else {
            this.registerClient(); // Just set up the streams now, there is no local work.
        }
    }

    public endVideo() {
        if (this.timeoutId) {
            workerTimers.clearTimeout(this.timeoutId);
        }
        if (this.client) {
            this.unRegistering = true;
            this.upStreamConnection && this.upStreamConnection.close();
            this.channel && this.channel.leave();
            this.client &&
                this.client.unregister().then(() => {
                    if (this.localMedia) {
                        this.localMedia.stop();
                        this.localMedia.destroy();
                        this.localMedia = null;
                    }
                    this.client = null;
                });
        } else {
            fm.liveswitch.Log.error("session is already exited");
        }
    }

    private registerClient() {
        fm.liveswitch.Log.info("Begin Client Registration");

        var useOnPremiseServer = this.onPremiseSettings.useOnPremiseServer;

        var gatewayUrl = useOnPremiseServer ? this.connector.onPremiseGatewayUrl : "https://cloud.liveswitch.io/";

        var cloudAppId = this.recordingEnabled
            ? this.connector.liveSwitchAppId
            : this.connector.nonRecordingLiveSwitchAppId;

        var appId = useOnPremiseServer ? this.onPremiseSettings.onPremiseAppId : cloudAppId;

        this.client = new fm.liveswitch.Client(gatewayUrl, appId, this.userId, this.deviceId!, null as any, this.roles);

        this.client.setUserAlias(this.userName);

        // get observer speakers if audio permission already granted
        if (this.isIntObserver || this.isExtObserver) {
            navigator.permissions.query({ name: "microphone" }).then((permission) => {
                if (permission.state == "granted") {
                    this.getAudioOutputDevices();
                }
            });
        }

        this.client.addOnStateChange((client) => {
            if (client.getState() === fm.liveswitch.ClientState.Registering) {
                fm.liveswitch.Log.debug("client is registering");
            } else if (client.getState() === fm.liveswitch.ClientState.Registered) {
                fm.liveswitch.Log.debug("client is registered");
                if (this.client === client) {
                    this.connector.updateNetworkStatus(true);
                }
            } else if (client.getState() === fm.liveswitch.ClientState.Unregistering) {
                fm.liveswitch.Log.debug("client is unregistering");
            } else if (client.getState() === fm.liveswitch.ClientState.Unregistered) {
                fm.liveswitch.Log.debug("client is unregistered");

                // Client has failed for some reason:
                // We do not need to `c.closeAll()` as the client handled this for us as part of unregistering.
                if (!this.unRegistering) {
                    let self = this;
                    if (this.client === client) {
                        this.connector.updateNetworkStatus(false);
                    }
                    setTimeout(function () {
                        // Back off our re-register attempts as they continue to fail to avoid runaway process.
                        if (self.reRegisterBackoff < self.maxRegisterBackoff) {
                            self.reRegisterBackoff += self.reRegisterBackoff;
                        }

                        // ReRegister                        
                        self.client!.register(self.token!).then((channels: fm.liveswitch.Channel[]) => {
                            self.reRegisterBackoff = 200; // reset for next time
                            self.registerChannel(channels);
                        });
                    }, this.reRegisterBackoff);
                }
            }
        });

        this.client
            .register(this.token!)
            .then((channels: fm.liveswitch.Channel[]) => this.registerChannel(channels))
            .fail(function (ex: any) {
                fm.liveswitch.Log.error("Client Registration Failed");
            });
    }

    public requestAudioOutputDevices() {
        this.getAudioOutputDevices();
    }

    private getAudioOutputDevices(): void {
        try {
            if (navigator && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                navigator.mediaDevices
                    .getUserMedia({ audio: true, video: false })
                    .then(() => {
                        navigator.mediaDevices.enumerateDevices().then((devices) => {
                            if (!devices) {
                                fm.liveswitch.Log.error("audio sinks did not return successfully");
                            }

                            this.connector.audioOutputDeviceListCallback(
                                devices
                                    .filter((d) => d.kind === "audiooutput")
                                    .map((d, i) => ({
                                        id: d.deviceId,
                                        name: d.label,
                                        selected: false,
                                    }))
                            );
                        });
                    })
                    .catch((err) => console.log("There was an error retrieving audio devices:", err));
            }
        } catch (ex) {
            fm.liveswitch.Log.error("Failed to get SINK device list", ex);
        }
    }

    private messageReceived(clientInfo: fm.liveswitch.ClientInfo, message: string) {
        if (clientInfo.getDeviceId() !== this.deviceId) {
            this.parseMessage(clientInfo, message);
        }
    }

    private registerChannel(channels: fm.liveswitch.Channel[]): void | fm.liveswitch.Future<unknown> {
        this.channel = channels[0];
        this.channel.addOnMessage((i, m) => this.messageReceived(i, m));
        this.channel.addOnDeviceMessage((i, m) => this.messageReceived(i, m));
        this.setupChannelStreams();
    }

    private initLayoutAndLocalView() {
        let constraints: MediaTrackConstraintSet[] = [];

        if (this.isClient || this.isExtObserver) {
            if (this.deviceType.toUpperCase() in clientConstraints) {
                constraints = clientConstraints[this.deviceType.toUpperCase()];
            } else if (this.operatingSystem.toUpperCase() in clientConstraints) {
                constraints = clientConstraints[this.operatingSystem.toUpperCase()];
            } else {
                constraints = clientConstraints.defaultSet;
            }
        } else {
            constraints = [
                {
                    width: this.mentorVideoWidth,
                    height: this.mentorVideoHeight,
                    aspectRatio: 4.0 / 3.0,
                    frameRate: this.mentorVideoFrameRate,
                } as MediaTrackConstraintSet,
            ];
        }
        var pluginConfig = new fm.liveswitch.PluginConfig();
        pluginConfig.setActiveXPath("./FM.LiveSwitch.ActiveX.cab");
        fm.liveswitch.Plugin.install(pluginConfig).then((result) => {
            /**
             * Attempts multiple constraint settings to get local media to start.
             * @param index The index we are currently trying from the defined constraints collection
             */
            const startMedia = (index: number) => {
                fm.liveswitch.Log.info("Trying Constraints: " + JSON.stringify(constraints[index]));
                var audio = {
                    echoCancellation: true,
                    autoGainControl: true,
                    noiseSuppression: true,
                } as MediaTrackConstraints;
                this.localMedia = new fm.liveswitch.LocalMedia(true, constraints[index]);
                this.localMedia.setAudioLevelInterval(1000);
                var pastAudioLevel_1 = -1;
                this.localMedia.addOnAudioLevel(function (audioLevel) {
                    if (Math.abs(audioLevel - pastAudioLevel_1) >= 0.01) {
                        fm.liveswitch.Log.info(
                            "Local audio level is now " +
                            fm.liveswitch.Utility.formatDoubleAsPercent(audioLevel, 0) +
                            "%."
                        );
                        pastAudioLevel_1 = audioLevel;
                    }
                });
                (window as any).localMedia = this.localMedia;
                // Check if this browser is supported with local media (if not receive-only).
                if (!fm.liveswitch.Plugin.isReady(true)) {
                    // Check if this browser is supported without local media.
                    if (fm.liveswitch.Plugin.isReady()) {
                        alert("Your web browser is not capable of doing a browser based video call.");
                    } else {
                        alert("Your web browser is not capable of doing a browser based video call.");
                    }
                    return;
                }

                this.localMedia!.start()
                    .then((lm: fm.liveswitch.LocalMedia) => {
                        fm.liveswitch.Log.info("Successfully started local media");

                        this.localMedia?.getViewSink().setViewMirror(false);
                        //We reference the video inputs when creating out bound connection so lets make sure we have already
                        //Fulfilled the promise.
                        this.localMedia!.getVideoSourceInputs()
                            .then((inputs) => {
                                if (!inputs) {
                                    fm.liveswitch.Log.error("video source inputs did not return successfully");
                                } else {
                                    fm.liveswitch.Log.info("Received video source inputs.");
                                }
                                this.localVideoInputs = inputs;
                                this.connector.localVideoDeviceListCallback(
                                    this.localVideoInputs!.map((v, i) => ({
                                        id: v.getId(),
                                        name: v.getName(),
                                        selected: i === 0,
                                    }))
                                );

                                let currentAudioSourceInput = this.localMedia!.getAudioSourceInput();

                                if (currentAudioSourceInput === null) {
                                    alert("Microphone not successfully accessed");
                                }
                                try {
                                    this.localMedia!.getAudioSourceInputs().then((inputs) => {
                                        if (!inputs) {
                                            fm.liveswitch.Log.error("audio source inputs did not return successfully");
                                        }
                                        this.localAudioInputs = inputs;
                                        this.connector.audioDeviceListCallback(
                                            inputs.map((a, i) => ({
                                                id: a.getId(),
                                                name: a.getName(),
                                                selected:
                                                    currentAudioSourceInput != null &&
                                                    currentAudioSourceInput.getId() == a.getId(),
                                            }))
                                        );
                                    });
                                } catch (ex) {
                                    fm.liveswitch.Log.error("Failed to get audio input device list", ex);
                                }

                                this.getAudioOutputDevices();
                                this.myVideo!.append(this.localMedia!.getView());
                                this.videoElement = this.myVideo?.querySelector("video")!;

                                if (this.isClient || this.isExtObserver) {
                                    this.initVideoToReducedCanvas();
                                }
                                this.registerClient();
                            })
                            .fail((ex: any) => {
                                fm.liveswitch.Log.error("Failed to get video device list", ex);
                            });
                    })
                    .fail((ex: any) => {
                        fm.liveswitch.Log.warn("Failed to start local media " + ex.message);
                        if (index + 1 < constraints.length) {
                            fm.liveswitch.Log.info("Attempting next set of constraints");
                            startMedia(index + 1);
                        } else {
                            alert("We are having trouble starting your camera " + ex.message);
                        }
                    });
            };

            startMedia(0);
        });
    }

    private setupChannelStreams() {
        fm.liveswitch.Log.info("[LiveSwitch] Setting up channel stream...");
        console.log("[LiveSwitch] setting up channel stream");
        //Add hook to register new connections
        this.channel.addOnRemoteUpstreamConnectionOpen((remoteConnectionInfo) => {
            this.openSfuDownstreamConnection(remoteConnectionInfo);
        });

        //Add local feed as Outbound Upstream
        if (this.myVideo !== null) {
            this.openSfuUpstreamConnection(); //TODO: no upstream for observer
        }
        fm.liveswitch.Log.info("[LiveSwitch] making downstream connections...");
        for (let remoteConnectionInfo of this.channel.getRemoteUpstreamConnectionInfos()) {
            fm.liveswitch.Log.info("[LiveSwitch] Connecting: " + remoteConnectionInfo);
            console.log("[LiveSwitch] Connecting: ", remoteConnectionInfo)
            this.openSfuDownstreamConnection(remoteConnectionInfo);
        }
    }

    private openSfuDownstreamConnection(remoteConnectionInfo: fm.liveswitch.ConnectionInfo) {
        let isClient = false;
        if (
            remoteConnectionInfo.getClientRoles().indexOf("Client") !== -1 ||
            remoteConnectionInfo.getClientRoles().indexOf("ExternalObserver") !== -1
        ) {
            isClient = true;
            fm.liveswitch.Log.info("parsing device list from tag");
            let devices = JSON.parse(remoteConnectionInfo.getTag());
            this.connector.remoteVideoDeviceListCallback(devices);
        }

        var remoteMedia = new fm.liveswitch.RemoteMedia();

        // Set video based on observer vs mentor
        if (this.myVideo || isClient) {
            this.layoutManager.addRemoteView(remoteMedia.getId(), remoteMedia.getView());

            if (this.myVideo && this.enableAI) {
                this.registerVideoForAI(remoteMedia.getView() as HTMLDivElement);
            }
        } else {
            var currentView = remoteMedia.getView();
            var newDiv = document.createElement("div");

            //get mentor user info from client and inject into remote video frame
            var userName = remoteConnectionInfo.getUserAlias();
            var html = "<div>" + userName + "</div>";
            newDiv.innerHTML = html;
            newDiv.style.cssText =
                "text-align:center; color:#ffffff;position:absolute;bottom:0;left:0;padding:5px;min-width:100%;text-shadow: 1.2px 1.2px #333;";

            currentView.appendChild(newDiv);

            this.layoutManager.setLocalView(remoteMedia.getView());
        }

        if (!remoteConnectionInfo.getHasVideo()) {
            fm.liveswitch.Log.warn("In bound stream does not have video stream");
        }

        if (!remoteConnectionInfo.getHasAudio()) {
            fm.liveswitch.Log.warn("In bound stream does not have audio stream");
        }
        var audioStream = new fm.liveswitch.AudioStream(remoteMedia);
        var videoStream = new fm.liveswitch.VideoStream(remoteMedia);
        var connection = this.channel.createSfuDownstreamConnection(remoteConnectionInfo, audioStream, videoStream);
        fm.liveswitch.Log.info("Checking for UDP settings on Downstream: " + this.skipUDPConnections);
        console.log("[LiveSwitch] Checking for UDP settings on Downstream: ", this.skipUDPConnections)
        if (this.skipUDPConnections) {
            connection.addOnAutomaticIceServers((con, iceServers) => {
                console.log("[LiveSwitch] Skipping UDP Connection on Downstream...");
                fm.liveswitch.Log.warn("[LiveSwitch] Skipping UDP Connection on Downstream...");
                let iceServerValues = iceServers.getValues();
                for (var i = 0; i < iceServerValues.length; i++) {
                    const iceServer = iceServerValues[i];
                    // Remove any ICE servers that aren't TURNS 
                    if (!iceServer.getIsTurn() || !iceServer.getIsTcp() || !iceServer.getIsSecure()) {
                        fm.liveswitch.Log.info("[LiveSwitch] Removing ice server: " + iceServer.getUrl());
                        console.log("[LiveSwitch] removing ice server: ", iceServer.getUrl())
                        iceServers.remove(iceServer);
                    }
                }
            });
            connection.setIceGatherPolicy(fm.liveswitch.IceGatherPolicy.Relay); // Only gather relay candidates
        }

        this.remoteMedia = remoteMedia;

        this.sfuDownstreamConnections[connection.getId()] = {
            connection,
            info: remoteConnectionInfo,
        };

        connection.addOnStateChange((c) => {
            var state = c.getState();

            fm.liveswitch.Log.info("Remote Connection state changed " + fm.liveswitch.ConnectionState[state]);

            if (state === fm.liveswitch.ConnectionState.Closing || state === fm.liveswitch.ConnectionState.Failing) {
                if (connection.getRemoteClosed()) {
                    fm.liveswitch.Log.info(connection.getId() + ": Media server closed the connection.");
                }
                if (this.myVideo || isClient) {
                    this.layoutManager.removeRemoteView(remoteMedia.getId());
                } else {
                    this.layoutManager.unsetLocalView();
                }
                delete this.sfuDownstreamConnections[c.getId()];
                if (Object.keys(this.sfuDownstreamConnections).length === 0) {
                    this.connector.setSessionActive(false);
                }
            } else if (state === fm.liveswitch.ConnectionState.Failed) {
                // Note: no need to close the connection as it's done for us.
                this.openSfuDownstreamConnection(remoteConnectionInfo);
            }
        });

        (connection as any).addOnQuality((q: any) => {
            this.connector.connectionStatusReceived(q.receiveRating, this.deviceId!);
        });

        connection
            .open()
            .then((result) => {
                fm.liveswitch.Log.info("Remote downstream connection opened");
                this.connector.setSessionActive(true);
            })
            .fail(function (ex) {
                fm.liveswitch.Log.error("an error occurred opening a connection", ex);
            });
    }

    registerVideoForAI(videoElement: HTMLDivElement) {
        if (this.aiInterval) {
            window.clearInterval(this.aiInterval);
            this.aiInterval = null;
        }

        if (!videoElement) {
            return;
        }
        this.remoteVideoElement = videoElement.firstChild as HTMLVideoElement;
        if (!this.aiCanvas) {
            this.aiCanvas = document.createElement("canvas");
            this.ai2dContext = this.aiCanvas.getContext("2d")!;
        }

        this.aiInterval = window.setInterval(() => this.grabImageForAI(), this.connector.videoAIFrameSampleRate);
    }

    grabImageForAI() {
        if (!this.remoteVideoElement) {
            if (this.aiInterval) {
                window.clearInterval(this.aiInterval);
                this.aiInterval = null;
            }
            return;
        } else if (this.remoteVideoElement.srcObject && !(this.remoteVideoElement.srcObject as any).active) {
            return;
        }

        var ratio = 1.0;

        var dateTaken = DateTime.utc().toJSDate();

        this.aiCanvas.width = this.remoteVideoElement.videoWidth * ratio;
        this.aiCanvas.height = this.remoteVideoElement.videoHeight * ratio;
        this.ai2dContext.drawImage(
            this.remoteVideoElement!,
            0,
            0,
            this.remoteVideoElement.videoWidth * ratio,
            this.remoteVideoElement.videoHeight * ratio
        );

        var imgData = this.aiCanvas.toBlob((blob) => {
            this.ai2dContext.clearRect(0, 0, this.aiCanvas.width, this.aiCanvas.height);
            if (blob !== null) this.connector.imageStillForAI(blob, dateTaken);
        }, "image/jpeg");
    }

    private openSfuUpstreamConnection(tag?: string): fm.liveswitch.SfuUpstreamConnection {
        let connection: fm.liveswitch.SfuUpstreamConnection;

        let audioStream!: fm.liveswitch.AudioStream;
        let videoStream!: fm.liveswitch.VideoStream;

        let vidMedia = this.canvasLocalMedia ?? this.localMedia!;

        if (this.localMedia!.getAudioTrack() != null) {
            audioStream = new fm.liveswitch.AudioStream(this.localMedia!);
        } else {
            fm.liveswitch.Log.error("Local audio track not found to stream");
            alert("Audio from mic not found.");
        }

        if (vidMedia.getVideoTrack() != null) {
            videoStream = new fm.liveswitch.VideoStream(vidMedia);
        } else {
            fm.liveswitch.Log.error("Local video track not found to stream");
            alert("Video source from camera not found.");
        }

        fm.liveswitch.Log.info("Creating Upstream connection");
        connection = this.channel.createSfuUpstreamConnection(audioStream, videoStream);
        fm.liveswitch.Log.info("Checking for UDP settings on Upstream: " + this.skipUDPConnections);
        console.log("[LiveSwitch] Checking for UDP settings on Upstream: ", this.skipUDPConnections)
        if (this.skipUDPConnections) {
            connection.addOnAutomaticIceServers((con, iceServers) => {
                console.log("[LiveSwitch] Skipping UDP Connection on Upstream...");
                fm.liveswitch.Log.warn("[LiveSwitch] Skipping UDP Connection on Upstream...");
                let iceServerValues = iceServers.getValues();
                for (var i = 0; i < iceServerValues.length; i++) {
                    const iceServer = iceServerValues[i];
                    // Remove any ICE servers that aren't TURNS 
                    if (!iceServer.getIsTurn() || !iceServer.getIsTcp() || !iceServer.getIsSecure()) {
                        fm.liveswitch.Log.info("[LiveSwitch] Removing ice server: " + iceServer.getUrl());
                        console.log("[LiveSwitch] removing ice server: ", iceServer.getUrl())
                        iceServers.remove(iceServer);
                    }
                }
            });
            connection.setIceGatherPolicy(fm.liveswitch.IceGatherPolicy.Relay); // Only gather relay candidates
        }

        connection.setTag(
            JSON.stringify(
                this.localVideoInputs!.map((v, i) => ({
                    id: v.getId(),
                    name: v.getName(),
                    selected: i === 0,
                }))
            )
        );

        connection.addOnStateChange((c) => {
            if (c.getState() === fm.liveswitch.ConnectionState.Failed) {
                // Note: no need to close the connection as it's done for us.
                this.openSfuUpstreamConnection();
            }
        });

        fm.liveswitch.Log.info("Opening Upstream connection");
        connection
            .open()
            .then(() => { })
            .fail(function (ex) {
                fm.liveswitch.Log.error("OPEN FAILURE " + ex.name + " " + ex.message);
                fm.liveswitch.Log.error("an error occurred opening a SFU upstream connection", ex);
            });

        this.upStreamConnection = connection;
        return connection;
    }

    initializeImageCaptureAsync(): Promise<ImageCapture | undefined> {
        var self = this;

        return new Promise(function executorIIC(resolve, reject) {
            fm.liveswitch.Log.info("Attempt Image Capture Init.");

            var q = qs.parse(window.location.search, { ignoreQueryPrefix: true });
            if (q.nocapture) {
                resolve(undefined);
                return;
            }

            if ((window as any).ImageCapture) {
                fm.liveswitch.Log.info("Getting Track Info");
                var track = (self.localMedia!.getVideoTrack() as any)._internal._getMediaStreamTrack();
                var capabilities = track.getCapabilities();

                try {
                    track.applyConstraints({
                        width: { max: 1600, min: capabilities.width.min, ideal: 1600 },
                        height: { max: 1200, min: capabilities.height.min, ideal: 1200 }
                    }).then(() => {
                        fm.liveswitch.Log.info("Instantiating Image Capture");
                        resolve(new ImageCapture(track));
                    }).catch((err: any) => {
                        fm.liveswitch.Log.warn("Issue applying width and height constraints to video track", err)
                        reject(new DOMException("Failed to apply track constraints."));
                    });
                } catch (error) {
                    fm.liveswitch.Log.warn("Issue creating image capture", error);
                    reject(new DOMException("ImageCapture initialization failed."));
                }
            }
        });
    }

    initializeImageCapture() {
        var self = this;
        fm.liveswitch.Log.info("Attempt Image Capture Init.");

        var q = qs.parse(window.location.search, { ignoreQueryPrefix: true });
        if (q.nocapture) {
            return;
        }

        if ((window as any).ImageCapture) {
            fm.liveswitch.Log.info("Getting Track Info");
            var track = (self.localMedia!.getVideoTrack() as any)._internal._getMediaStreamTrack();

            try {
                track.applyConstraints({
                    width: { exact: 1600 },
                    height: { exact: 1200 }
                }).then(() => {
                    fm.liveswitch.Log.info("Instantiating Image Capture");
                    self.imageCapture = new ImageCapture(track);
                }).catch((err: any) =>
                    fm.liveswitch.Log.warn("Issue applying width and height constraints to video track", err)
                );
            } catch (e) {
                fm.liveswitch.Log.warn("Issue creating image capture", e);
            }
        }
    }

    private initVideoToReducedCanvas() {
        this.drawStreamingCanvas();
        let stream = (this.streamingCanvasElement as any).captureStream(20);
        this.canvasLocalMedia = new fm.liveswitch.LocalMedia(false, stream);
        this.canvasLocalMedia.start();
    }

    private streamQualityToRatioOfCurrentHeight(quality: StreamQuality, currentVideoHeight: number): number {
        var ratio = 1;
        switch (quality) {
            case StreamQuality.a_256by144: {
                ratio = 144 / currentVideoHeight;
                break;
            }
            case StreamQuality.b_512by288: {
                ratio = 288 / currentVideoHeight;
                break;
            }
            case StreamQuality.c_768by432: {
                ratio = 432 / currentVideoHeight;
                break;
            }
            case StreamQuality.d_1024by576: {
                ratio = 576 / currentVideoHeight;
                break;
            }
            case StreamQuality.e_1280by720:
            default: {
                ratio = 720 / currentVideoHeight;
                break;
            }
        }
        if (ratio > 1) {
            return 1;
        }
        return ratio;
    }

    private drawStreamingCanvas() {
        if (this.videoElement && this.videoElement?.videoWidth !== 0) {
            var ratio = this.streamQualityToRatioOfCurrentHeight(
                this.canvasStreamQuality,
                Math.min(this.videoElement.videoHeight, this.videoElement.videoWidth)
            );
            this.streamingCanvasElement.width = this.videoElement.videoWidth * ratio;
            this.streamingCanvasElement.height = this.videoElement.videoHeight * ratio;
            this.streamingCanvas2dContext.drawImage(
                this.videoElement!,
                0,
                0,
                this.videoElement.videoWidth * ratio,
                this.videoElement.videoHeight * ratio
            );
        }

        //DO NOT REMOVE: necessary for iOS 15.1+ to work (GS-185)
        this.info.innerHTML = ".";

        // requestAnimationFrame and setTimeout do not fire when window is not focused so using web worker.
        this.timeoutId = workerTimers.setTimeout(() => this.drawStreamingCanvas(), 1000 / 30);
    }

    private updateTakingPhoto(busy: boolean) {
        this.takingPhoto = busy;
        this.connector.updateCameraBusyStatus(busy);
    }

    /**
     * Sets the quality reducer level on canvas for devices without Image Capture
     * @param quality A number 1-5.  One being low and 5 high quality
     */
    public setQuality(quality: number) {
        if (!this.isClient) {
            return;
        }
        this.canvasStreamQuality = quality;
    }

    public takePhoto(id: string) {
        var self = this;

        if (self.takingPhoto || !self.isClient) {
            return;
        }

        self.updateTakingPhoto(true);

        // Temporarily force the app to use takePhotoWithCanvas() as a workaround
        // for bugs in the Safari 18.4 implementation of the Image Capture API. 
        // This workaround needs to remain in place until the Safari bugs are addressed and
        // no clients use Safari 18.4.
        if (false) {
            //if ((window as any).ImageCapture !== undefined) {
            fm.liveswitch.Log.info("Taking photo - " + id + " on LiveSwitch");
            self.initializeImageCaptureAsync().then((imageCapture) => {
                if (imageCapture) {
                    self.imageCapture = imageCapture;

                    self.imageCapture
                        .takePhoto()
                        .then((blob: Blob) => {
                            self.connector.photoTakenCallback(id, blob);
                        })
                        .finally(() => self.updateTakingPhoto(false));
                }
            }).catch((error: any) => {
                fm.liveswitch.Log.error("Error taking photo with image capture API", error);
            });
        } else {
            fm.liveswitch.Log.info("Taking photo - " + id + " with canvas on LiveSwitch");
            self.takePhotoWithCanvas()
                .then((blob) => {
                    self.connector.photoTakenCallback(id, blob);
                    self.updateTakingPhoto(false);
                })
                .finally(() => self.updateTakingPhoto(false));
        }
    }

    takePhotoWithCanvas(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            var videoElement = this.myVideo!.querySelector("video") as HTMLVideoElement;
            this.canvasElement.width = videoElement.videoWidth;
            this.canvasElement.height = videoElement.videoHeight;
            try {
                this.canvas2dContext!.drawImage(videoElement, 0, 0);
                this.canvasElement.toBlob((blob) => {
                    this.canvas2dContext.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
                    if (blob === null) reject(new Error("Error generating blob from canvas"));
                    else resolve(blob);
                }, "image/jpeg");
            } catch (ex) {
                reject(new Error("Couldn't generate image from stream"));
            }
        });
    }

    public sendMessage(text: string) {
        this.channel.sendMessage(text);
    }

    public setActiveLocalCamera(id: string) {
        this.setLocalVideoInput(id);
    }
    public setActiveLocalAudio(id: string) {
        this.setLocalAudioInput(id);
    }

    public sendClientConnectionQualityWarning() {
        if (this.isClientConnectionQualityWarningIssued) {
            return;
        }

        this.connector.sendClientConnectionQualityWarning();
        this.isClientConnectionQualityWarningIssued = true;
    }

    public cancelClientConnectionQualityWarning() {
        if (!this.isClientConnectionQualityWarningIssued) {
            return;
        }

        this.connector.cancelClientConnectionQualityWarning();
        this.isClientConnectionQualityWarningIssued = false;
    }

    parseMessage(clientInfo: fm.liveswitch.ClientInfo, message: string) {
        if (message.startsWith(CommandPrefix)) {
            let cmd = message.substring(CommandPrefix.length, message.indexOf(":")) as ChatCommand;
            let options = JSON.parse(message.substring(message.indexOf(":") + 1));

            switch (cmd) {
                case ChatCommand.TakePhoto: {
                    if (!this.takingPhoto) {
                        this.connector.photoRequestReceived(options.id);
                        this.takePhoto(options.id);
                    } else {
                        this.sendPhotoURLCommand(options.id, "", "");
                    }
                    break;
                }
                case ChatCommand.PhotoUrl: {
                    this.connector.receivedPhotoUrlFromClient(options.id, options.url, options.thumbnailUrl);
                    break;
                }
                case ChatCommand.ClientMuteChanged: {
                    this.connector.sendClientAudioMuteUpdated(options.muted);
                    break;
                }
                case ChatCommand.VideoFrameTagPhotoUrl: {
                    this.connector.receivedVideoFrameTagPhotoUrlFromMentor(options.orderId, options.photo);
                    break;
                }
                case ChatCommand.SwitchCamera: {
                    if (this.localMedia?.getVideoInput().getId() === options.id) {
                        return;
                    }
                    this.connector.confirmClientCameraChange && this.connector.confirmClientCameraChange(options.id);
                    break;
                }
                case ChatCommand.ToggleRecording: {
                    this.connector.recordingToggled(options.isRecording);
                    break;
                }
                case ChatCommand.MuteCamera: {
                    this.connector.confirmClientCameraMuteToggle && this.connector.confirmClientCameraMuteToggle();
                    break;
                }
                case ChatCommand.MuteAudio: {
                    this.connector.confirmClientAudioMuteToggle && this.connector.confirmClientAudioMuteToggle();
                    break;
                }
                case ChatCommand.SessionEnded: {
                    this.connector.videoSessionEndedCallback();
                    break;
                }
                case ChatCommand.PhotoRemoved: {
                    this.connector.removeDeletedPhoto(options.photoId);
                    break;
                }
                case ChatCommand.CanvasQuality: {
                    this.setQuality(options.quality);
                    break;
                }
                case ChatCommand.ClientConnectionQualityWarning: {
                    this.sendClientConnectionQualityWarning();
                    break;
                }
                case ChatCommand.ClientConnectionQualityOk: {
                    this.cancelClientConnectionQualityWarning();
                    break;
                }
            }
            return;
        }
        this.connector.messageReceivedCallback(clientInfo, message);
    }

    private setLocalVideoInput(id: string) {
        if (this.localVideoInputs) {
            this.localMedia?.changeVideoSourceInput(this.localVideoInputs.find((v) => v.getId() === id)!);
            this.localMedia?.getViewSink().setViewMirror(false);
        }
    }

    private setLocalAudioInput(id: string) {
        if (this.localAudioInputs) {
            let input = this.localAudioInputs.find((v) => v.getId() === id)!;
            this.localMedia?.changeAudioInput(input);
        }
    }

    public setLocalAudioOutput(id: string, name: string) {
        this.remoteMedia?.setAudioSinkOutput(new fm.liveswitch.SinkOutput(id, name));
    }

    public sendRequestPhotoCommand(id: string) {
        if (!this.isExtObserver) {
            fm.liveswitch.Log.info("Sending request photo - " + id + " command to client from mentor via LiveSwitch");
            this.sendCommand(ChatCommand.TakePhoto, true, { id });
        }
    }

    public sendPhotoURLCommand(id: string, url: string, thumbnailUrl: string) {
        this.sendCommand(ChatCommand.PhotoUrl, false, { id, url, thumbnailUrl });
    }

    public sendVideoFrameTagPhotoUrlCommand(orderId: string, photo: SessionPhoto) {
        this.sendCommand(ChatCommand.VideoFrameTagPhotoUrl, false, { orderId, photo });
    }

    public sendSessionEndedCommand() {
        this.sendCommand(ChatCommand.SessionEnded, false);
    }

    public sendRemoveDeletedPhotoCommand(photoId: string) {
        this.sendCommand(ChatCommand.PhotoRemoved, true, { photoId });
    }

    public sendCanvasQualityCommand(quality: number) {
        this.sendCommand(ChatCommand.CanvasQuality, true, { quality });
    }

    public sendToggleCameraCommand(cameraId: string, cameraName: string) {
        this.sendCommand(ChatCommand.SwitchCamera, true, {
            name: cameraName,
            id: cameraId,
        });
    }

    public sendToggleRecordingCommand(isRecording: boolean) {
        this.sendCommand(ChatCommand.ToggleRecording, true, {
            isRecording,
        });
    }

    public sendToggleMuteCameraCommand() {
        this.sendCommand(ChatCommand.MuteCamera, true);
    }

    public sendClientMuteChangedCommand(muted: boolean) {
        this.sendCommand(ChatCommand.ClientMuteChanged, false, { muted }, true);
    }

    public sendToggleMuteAudioCommand() {
        this.sendCommand(ChatCommand.MuteAudio, true);
    }

    public sendClientConnectionQualityWarningCommand() {
        this.sendCommand(ChatCommand.ClientConnectionQualityWarning, false);
    }

    public cancelClientConnectionQualityWarningCommand() {
        this.sendCommand(ChatCommand.ClientConnectionQualityOk, false);
    }

    private sendCommand(commandMessage: ChatCommand, toClient: boolean, options?: any, toMentor: boolean = false) {
        let command =
            CommandPrefix +
            commandMessage +
            ":" +
            (options === undefined ? JSON.stringify(null) : JSON.stringify(options));

        //Commands are sent to recipients based on type
        for (let key in this.sfuDownstreamConnections) {
            if (this.sfuDownstreamConnections.hasOwnProperty(key)) {
                let info = this.sfuDownstreamConnections[key].info;
                let isClientConnection = info.getClientRoles().indexOf("Client") !== -1;
                let isMentorConnection = info.getClientRoles().indexOf("Mentor") !== -1;
                let isObserver = this.isIntObserver || this.isExtObserver;

                //Do not send commands to self, or if we're an observer
                if (info.getDeviceId() === this.deviceId || isObserver) {
                    return;
                }

                if ((toClient && isClientConnection) || (!toClient && !toMentor) || (toMentor && isMentorConnection)) {
                    this.channel.sendMessage(command);
                }
            }
        }
    }

    toggleLocalMediaVideoMute() {
        let videoTrack = this.localMedia!.getVideoTrack();
        if (!videoTrack) {
            return false;
        }
        videoTrack.setMuted(!videoTrack.getMuted());
    }

    toggleLocalMediaAudioMute() {
        let audioTrack = this.localMedia!.getAudioTrack();
        if (!audioTrack) {
            return false;
        }
        audioTrack.setMuted(!audioTrack.getMuted());
    }
}

export default new LiveSwitchApp();
